import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import { SmtpCard } from "./SmtpCard";
import { ScreenTips } from "@/components/GuidedHighlights";
import { COACHMARKS } from "@/lib/coachmarks";
import { Building2, School, Ticket, Loader2, Plus, Lock, ArrowRight, Copy, UserPlus, FileText, BarChart3, Activity, Users, GraduationCap, Mail, Clock, RefreshCcw } from "lucide-react";
import PurchaseOrderModal from "./PurchaseOrderModal";

const inputClass = "w-full rounded-2xl border-2 border-[#E5DECF] bg-white px-4 py-3 font-body text-[#1F2937] outline-none focus:border-[#0D9488]";

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="rounded-3xl border border-[#E5DECF] bg-white p-5">
      <div className="flex items-center gap-2 text-[#7A3FF2]"><Icon size={18} /><span className="text-xs uppercase tracking-wide font-semibold text-[#6B7280]">{label}</span></div>
      <div className="mt-1 font-heading text-3xl font-extrabold text-[#1F2937]">{value}</div>
    </div>
  );
}

function relTime(iso) {
  if (!iso) return "No activity yet";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(0, Math.round((now - then) / 1000));
  if (s < 60) return "Just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function FillBar({ percent, color = "#7A3FF2" }) {
  const p = Math.max(0, Math.min(100, percent || 0));
  const tone = p >= 90 ? "#10B981" : p >= 50 ? color : "#94A3B8";
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-[#E5DECF]">
      <div className="h-full rounded-full transition-all" style={{ width: `${p}%`, backgroundColor: tone }} />
    </div>
  );
}

function DistrictRollup({ rollup, busy, onRefresh }) {
  if (!rollup || !rollup.district) return null;
  const t = rollup.totals || {};
  const schools = rollup.schools || [];
  return (
    <div className="mt-6 rounded-3xl border border-[#E5DECF] bg-white p-6" data-testid="district-rollup">
      <div className="flex items-center gap-2">
        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#7A3FF2]/10 text-[#7A3FF2]"><BarChart3 size={20} /></div>
        <div className="flex-1">
          <h2 className="font-heading text-lg font-bold text-[#1F2937]">District rollup · Live usage</h2>
          <p className="font-body text-xs text-[#6B7280]">See which schools are filling seats and driving learning activity.</p>
        </div>
        <button onClick={onRefresh} disabled={busy} data-testid="rollup-refresh" className="rounded-full border-2 border-[#E5DECF] px-3 py-1.5 text-sm font-bold text-[#1F2937] hover:border-[#7A3FF2] disabled:opacity-50">
          <RefreshCcw size={14} className={`inline-block mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <div className="rounded-2xl border border-[#E5DECF] p-3" data-testid="rollup-seats"><div className="text-xs uppercase font-bold text-[#7A3FF2]">Seats used</div><div className="font-heading text-xl font-extrabold">{t.seats_used || 0}<span className="text-sm text-[#94A3B8] font-body font-normal"> / {t.seats_pool || 0}</span></div></div>
        <div className="rounded-2xl border border-[#E5DECF] p-3" data-testid="rollup-teachers"><div className="text-xs uppercase font-bold text-[#0D9488]">Teachers active</div><div className="font-heading text-xl font-extrabold">{t.teachers_active || 0}</div></div>
        <div className="rounded-2xl border border-[#E5DECF] p-3" data-testid="rollup-invites"><div className="text-xs uppercase font-bold text-[#FF9E1B]">Invites pending</div><div className="font-heading text-xl font-extrabold">{t.invites_pending || 0}</div></div>
        <div className="rounded-2xl border border-[#E5DECF] p-3" data-testid="rollup-students"><div className="text-xs uppercase font-bold text-[#0284C7]">Students</div><div className="font-heading text-xl font-extrabold">{t.students_total || 0}</div></div>
        <div className="rounded-2xl border border-[#E5DECF] p-3" data-testid="rollup-active"><div className="text-xs uppercase font-bold text-[#10B981]">Active last 7d</div><div className="font-heading text-xl font-extrabold">{t.students_active_7d || 0}</div></div>
      </div>

      <div className="mt-4">
        {schools.length === 0 ? (
          <div className="rounded-2xl bg-[#FAF7F2] p-6 text-center font-body text-sm text-[#6B7280]" data-testid="rollup-empty">
            Add a school above to start seeing per-school usage here.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm" data-testid="rollup-table">
              <thead>
                <tr className="text-left text-xs uppercase text-[#94A3B8]">
                  <th className="py-2 pr-3">School</th>
                  <th className="pr-3">Seats</th>
                  <th className="pr-3">Fill</th>
                  <th className="pr-3">Teachers</th>
                  <th className="pr-3">Pending</th>
                  <th className="pr-3">Students</th>
                  <th className="pr-3">Active 7d</th>
                  <th className="pr-3">Last activity</th>
                </tr>
              </thead>
              <tbody>
                {schools.map((s) => (
                  <tr key={s.id} className="border-t border-[#F2ECE1]" data-testid={`rollup-school-${s.id}`}>
                    <td className="py-3 pr-3">
                      <div className="flex items-center gap-2 font-bold text-[#1F2937]"><School size={14} className="text-[#7A3FF2]" /> {s.name}</div>
                      {!s.admin_count && <div className="text-xs font-semibold text-[#B45309]">No admin invited yet</div>}
                    </td>
                    <td className="pr-3 font-mono text-sm">{s.seats_used}/{s.seats}</td>
                    <td className="pr-3 min-w-[140px]">
                      <div className="flex items-center gap-2">
                        <div className="w-24"><FillBar percent={s.fill_percent} /></div>
                        <span className="text-xs font-bold text-[#6B7280]">{s.fill_percent}%</span>
                      </div>
                    </td>
                    <td className="pr-3"><span className="inline-flex items-center gap-1 rounded-full bg-[#0D9488]/10 px-2 py-0.5 text-xs font-bold text-[#0D9488]"><GraduationCap size={12} /> {s.teachers_active}</span></td>
                    <td className="pr-3"><span className="inline-flex items-center gap-1 rounded-full bg-[#FF9E1B]/10 px-2 py-0.5 text-xs font-bold text-[#B45309]"><Mail size={12} /> {s.invites_pending}</span></td>
                    <td className="pr-3"><span className="inline-flex items-center gap-1 rounded-full bg-[#0284C7]/10 px-2 py-0.5 text-xs font-bold text-[#0369A1]"><Users size={12} /> {s.students_total}</span></td>
                    <td className="pr-3"><span className="inline-flex items-center gap-1 rounded-full bg-[#10B981]/10 px-2 py-0.5 text-xs font-bold text-[#047857]"><Activity size={12} /> {s.students_active_7d}</span></td>
                    <td className="pr-3 text-xs text-[#6B7280]"><span className="inline-flex items-center gap-1"><Clock size={12} /> {relTime(s.last_activity_at)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function DistrictAdminHome() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [districtName, setDistrictName] = useState("");
  const [creating, setCreating] = useState(false);
  const [newSchool, setNewSchool] = useState({ name: "", seats: 5 });
  const [adding, setAdding] = useState(false);
  const [inviteFor, setInviteFor] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [lastLink, setLastLink] = useState(null);
  const [showPO, setShowPO] = useState(false);
  const [rollup, setRollup] = useState(null);
  const [rollupBusy, setRollupBusy] = useState(false);

  const loadRollup = async () => {
    setRollupBusy(true);
    try { setRollup(await api.getDistrictRollup()); }
    catch (e) { /* silent — rollup is optional */ }
    finally { setRollupBusy(false); }
  };

  const load = async () => { try { setData(await api.getMyDistrict()); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (data?.district) loadRollup(); /* eslint-disable-next-line */ }, [data?.district?.id]);

  if (loading) return <div className="p-16 text-center"><Loader2 className="mx-auto animate-spin text-[#7A3FF2]" /></div>;

  const active = data.subscription?.status === "active";
  const district = data.district;

  const createDistrict = async () => {
    if (!districtName.trim()) { toast.error("Name your district"); return; }
    setCreating(true);
    try { await api.createDistrict(districtName.trim()); toast.success("District created!"); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setCreating(false); }
  };

  const addSchool = async () => {
    if (!newSchool.name.trim()) { toast.error("Name the school"); return; }
    setAdding(true);
    try { await api.addDistrictSchool(newSchool.name.trim(), Number(newSchool.seats) || 0); toast.success("School added"); setNewSchool({ name: "", seats: 5 }); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setAdding(false); }
  };

  const updateSeats = async (school, seats) => {
    try { await api.setSchoolSeats(school.id, Number(seats)); toast.success("Seats updated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const inviteAdmin = async (schoolId) => {
    try {
      const r = await api.createInvite({ role: "school_admin", school_id: schoolId, email: inviteEmail.trim() || undefined });
      if (r.emailed) { toast.success(`Invite emailed to ${r.invite.email}`); setInviteFor(null); }
      else {
        await copyToClipboard(r.invite.link, "Admin invite link copied — share it");
        setLastLink({ schoolId, link: r.invite.link });
      }
      setInviteEmail(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#7A3FF2]"><Building2 size={14} /> District Admin</div>
      <h1 className="mt-1 font-heading text-3xl font-extrabold text-[#1F2937]">{district ? district.name : "Your District"}</h1>

      {!active && !district && (
        <div className="mt-6 rounded-3xl border-2 border-[#FF9E1B] bg-[#FF9E1B]/10 p-6" data-testid="district-upgrade-gate">
          <div className="flex items-center gap-2 text-[#B45309]"><Lock size={18} /><h2 className="font-heading text-lg font-extrabold">Activate your District plan</h2></div>
          <p className="mt-2 font-body text-sm text-[#6B7280]">Buy the School District plan to get a pool of teacher seats to allocate across your schools.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link to="/pricing" data-testid="district-choose-plan" className="inline-flex items-center gap-2 rounded-full bg-[#FF9E1B] px-6 py-3 font-heading font-bold text-[#1F2937] hover:brightness-95">Choose a plan <ArrowRight size={16} /></Link>
            <button onClick={() => setShowPO(true)} data-testid="district-po-btn" className="inline-flex items-center gap-2 rounded-full border-2 border-[#7A3FF2] bg-white px-6 py-3 font-heading font-bold text-[#7A3FF2] hover:bg-[#7A3FF2]/10">
              <FileText size={16} /> Pay by purchase order
            </button>
          </div>
        </div>
      )}

      {active && !district && (
        <div className="mt-6 rounded-3xl border border-[#E5DECF] bg-white p-6" data-testid="create-district-card">
          <h2 className="font-heading text-lg font-bold text-[#1F2937]">Name your district</h2>
          <div className="mt-3 flex gap-2">
            <input value={districtName} onChange={(e) => setDistrictName(e.target.value)} data-testid="district-name-input" className={inputClass} placeholder="e.g. Central Unified" />
            <button onClick={createDistrict} disabled={creating} data-testid="create-district-btn" className="inline-flex items-center gap-2 rounded-2xl bg-[#7A3FF2] px-5 py-3 font-bold text-white hover:brightness-95 disabled:opacity-50">
              {creating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Create
            </button>
          </div>
        </div>
      )}

      {district && (
        <>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            <Stat label="Seat pool" value={data.seats_total} icon={Ticket} />
            <Stat label="Allocated" value={data.seats_allocated} icon={School} />
            <Stat label="Unallocated" value={data.seats_unallocated} icon={Plus} />
          </div>

          <div className="mt-6 rounded-3xl border border-[#E5DECF] bg-white p-6">
            <h2 className="font-heading text-lg font-bold text-[#1F2937]">Add a school</h2>
            <p className="mt-1 font-body text-sm text-[#6B7280]">Give it a teacher-seat quota from your pool ({data.seats_unallocated} left).</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <input value={newSchool.name} onChange={(e) => setNewSchool((p) => ({ ...p, name: e.target.value }))} data-testid="new-school-name" className={`${inputClass} flex-1 min-w-[160px]`} placeholder="School name" />
              <input type="number" min="0" value={newSchool.seats} onChange={(e) => setNewSchool((p) => ({ ...p, seats: e.target.value }))} data-testid="new-school-seats" className="w-24 rounded-2xl border-2 border-[#E5DECF] px-3 py-3 font-body" />
              <button onClick={addSchool} disabled={adding} data-testid="add-school-btn" className="inline-flex items-center gap-2 rounded-2xl bg-[#7A3FF2] px-5 py-3 font-bold text-white hover:brightness-95 disabled:opacity-50">
                {adding ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Add
              </button>
            </div>
          </div>

          <DistrictRollup rollup={rollup} busy={rollupBusy} onRefresh={loadRollup} />

          {data.schools.length > 0 && (
            <div className="mt-6 space-y-3">
              {data.schools.map((s) => (
                <div key={s.id} className="rounded-3xl border border-[#E5DECF] bg-white p-5" data-testid={`dschool-${s.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h3 className="font-heading text-lg font-extrabold text-[#1F2937]">{s.name}</h3>
                      <p className="font-body text-sm text-[#6B7280]">{s.seats_used}/{s.seats} seats used · {s.admins.length} admin{s.admins.length === 1 ? "" : "s"}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-1 font-body text-xs text-[#6B7280]">Seats
                        <input type="number" min={s.seats_used} defaultValue={s.seats} onBlur={(e) => e.target.value != s.seats && updateSeats(s, e.target.value)} data-testid={`school-seats-${s.id}`} className="w-20 rounded-xl border-2 border-[#E5DECF] px-2 py-1.5 font-body" />
                      </label>
                      <button onClick={() => setInviteFor(inviteFor === s.id ? null : s.id)} data-testid={`invite-admin-${s.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#0D9488]/10 px-3 py-2 text-sm font-bold text-[#0D9488] hover:bg-[#0D9488]/20"><UserPlus size={14} /> Invite admin</button>
                    </div>
                  </div>
                  {inviteFor === s.id && (
                    <div className="mt-3 flex gap-2">
                      <input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} data-testid={`admin-email-${s.id}`} className={inputClass} placeholder="principal@email.com (optional)" />
                      <button onClick={() => inviteAdmin(s.id)} data-testid={`send-admin-invite-${s.id}`} className="inline-flex items-center gap-1.5 rounded-2xl bg-[#10B981] px-4 py-2.5 font-bold text-white hover:bg-[#059669]"><Copy size={14} /> Create invite</button>
                    </div>
                  )}
                  {lastLink && lastLink.schoolId === s.id && (
                    <input readOnly value={lastLink.link} data-testid={`dschool-invite-link-${s.id}`} onFocus={(e) => e.target.select()} className="mt-2 w-full select-all rounded-xl border border-[#E5DECF] bg-[#FAF7F2] px-3 py-1.5 font-mono text-xs text-[#6B7280] outline-none" />
                  )}
                  {s.admins.length > 0 && <div className="mt-2 font-body text-xs text-[#94A3B8]">Admins: {s.admins.map((a) => a.name || a.email).join(", ")}</div>}
                </div>
              ))}
            </div>
          )}

          <div className="mt-6"><SmtpCard /></div>

          <div className="mt-6 rounded-3xl border border-dashed border-[#7A3FF2]/40 bg-[#7A3FF2]/5 p-5" data-testid="district-po-upsell">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#7A3FF2]/10 text-[#7A3FF2]"><FileText size={18} /></div>
              <div className="flex-1">
                <div className="font-heading font-bold text-[#1F2937]">Need more seats — or paying by invoice?</div>
                <p className="mt-1 font-body text-sm text-[#6B7280]">Card processors reject charges above ~$40k. Submit a purchase order and we'll invoice your district by ACH, wire or check.</p>
              </div>
              <button onClick={() => setShowPO(true)} data-testid="district-po-btn-inline" className="inline-flex items-center gap-2 rounded-full bg-[#7A3FF2] px-5 py-2.5 font-heading font-bold text-white hover:bg-[#652BDB]">
                Submit PO
              </button>
            </div>
          </div>
        </>
      )}
      <PurchaseOrderModal open={showPO} onClose={() => setShowPO(false)} defaultOrg={district?.name || ""} onSubmitted={load} />
      <ScreenTips screenKey="district-home" steps={COACHMARKS["district-home"]} />
    </div>
  );
}
